"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import {
  BarChart3,
  Settings,
  Key,
  Globe,
  User,
  Play,
  Clock,
  Home,
  Terminal,
  Bot,
  Users,
  ShieldCheck,
  FileText,
  Library,
  Sliders,
  MessageSquare,
  FolderClosed,
  ServerCog,
  KeyRound,
  BookOpen,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { listPipelineRuns } from "@/lib/api";
import { useWorkspace } from "@/contexts/workspace-context";
import { useAuth } from "@/contexts/auth-context";

interface RecentRun {
  job_id: string;
  query?: string;
  user_query?: string;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const router = useRouter();
  const locale = useLocale();
  const tNav = useTranslations("nav");
  const tCommon = useTranslations("common");
  const { hasCapability } = useAuth();
  const { selected } = useWorkspace();
  const [recentRuns, setRecentRuns] = useState<RecentRun[]>([]);

  useEffect(() => {
    let current = true;
    setRecentRuns([]);
    if (!open || !selected) return;
    listPipelineRuns(0, 5, selected).then((res) => {
      if (current && res.ok && res.data?.items) setRecentRuns(res.data.items);
    }).catch(() => {});
    return () => { current = false; };
  }, [open, selected]);

  const navigate = (path: string) => {
    router.push(`/${locale}${path}`);
    onOpenChange(false);
  };

  // requiredCapability mirrors sidebar.tsx's gating exactly (Phase 6 of
  // [[rbac-permission-overhaul-initiative]]) — this palette previously had
  // zero role/capability awareness at all, so every page appeared for
  // every authenticated user regardless of whether they could actually do
  // anything there. UX only; the backend enforces the real 403 either way.
  const allPages = [
    { label: tNav("home"), icon: Home, path: "" },
    { label: tNav("runs"), icon: Play, path: "/runs" },
    { label: tNav("analytics"), icon: BarChart3, path: "/analytics" },
    { label: tNav("library"), icon: Library, path: "/library" },
    { label: tNav("domains"), icon: Globe, path: "/domains" },
    { label: "AI Models", icon: Bot, path: "/ai-models", requiredCapability: "ai_models.manage" },
    { label: "Agents", icon: Users, path: "/agents", requiredCapability: "agents.manage" },
    { label: "Document Keywords", icon: FileText, path: "/document-keywords", requiredCapability: "document_keywords.manage" },
    { label: tNav("prompts"), icon: MessageSquare, path: "/prompts" },
    { label: tNav("configs"), icon: Sliders, path: "/configs" },
    { label: tNav("logs"), icon: Terminal, path: "/logs" },
    { label: tCommon("apiKeys"), icon: Key, path: "/api-keys" },
    { label: "Workers", icon: ServerCog, path: "/workers", requiredCapability: "workers.view" },
    { label: "User Administration", icon: ShieldCheck, path: "/users", requiredCapability: "users.view" },
    { label: "Role Administration", icon: KeyRound, path: "/roles", requiredCapability: "roles.manage" },
    { label: "Knowledge Base", icon: BookOpen, path: "/knowledge-base", requiredCapability: "knowledge_base.manage" },
    { label: tNav("agentModelPool"), icon: Sliders, path: "/agent-model-pool" },
    { label: tCommon("profile"), icon: User, path: "/profile" },
  ];
  const pages = allPages.filter((page) => !page.requiredCapability || hasCapability(page.requiredCapability));

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title={tCommon("commandPalette")} description={tCommon("commandPaletteHelp")}>
      <CommandInput placeholder={tCommon("commandSearch")} />
      <CommandList>
        <CommandEmpty>{tCommon("commandEmpty")}</CommandEmpty>

        <CommandGroup heading={tCommon("commandPages")}>
          {pages.map((page) => (
            <CommandItem key={page.path} onSelect={() => navigate(page.path)} className="gap-2">
              <page.icon className="h-4 w-4 text-muted-foreground" />
              {page.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {recentRuns.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={tCommon("commandRecentRuns")}>
              {recentRuns.map((run) => (
                <CommandItem
                  key={run.job_id}
                  onSelect={() => navigate(`/runs/${run.job_id}`)}
                  className="gap-2"
                >
                  <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{run.query || run.user_query || run.job_id}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground font-mono shrink-0">
                    {run.job_id.slice(0, 8)}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
